import { useCallback, useEffect, useMemo, useState } from 'react'

import { DefaultVariableResult, VariableSelectFeatureFlag } from '@makyoapp/makyo-common'

import { IAppLayer } from '@/core/app-builder/app/layer'
import { globalVariableClient } from '@/core/app-builder/global-variables'
import { parseVariableSelect } from '@/core/app-builder/parser/variable-select'
import { useAppAuthContext } from '@/context/__project__/AppAuthContext'
import { useCompContext } from '@/context/__project__/CompContext'
import { useDbClientContext } from '@/context/__project__/DbClientContext'

import { useTableSubscription } from './useTableSubscription'

/**
 * New Variable Select Parser Hook
 */
export function useParseVariableSelect<T>(
  variable?: DefaultVariableResult,
  client?: IAppLayer,
  tblId?: string,
  rowIdx?: number,
  rowId?: string
) {
  const { dbClient, language, userSpecific } = useDbClientContext()
  const { userEmail } = useAppAuthContext()
  const { detailTableId, rowId: detailRowId } = useCompContext()

  const [value, setValue] = useState<T>()

  // get related table id
  const tableIds = useMemo(() => {
    let tableId = ''

    switch (variable?.flag) {
      case VariableSelectFeatureFlag.SingleCell: {
        tableId = variable.tbl ?? ''
        break
      }
      case VariableSelectFeatureFlag.ProfileData: {
        tableId = window.appUserTableID ?? ''
        break
      }
      default:
        tableId = detailTableId ?? ''
    }

    return tableId ? [tableId] : []
    //
  }, [detailTableId, variable])

  // parse variable select fn
  const handleParseVariableSelect = useCallback(() => {
    const value = parseVariableSelect(variable, dbClient, userEmail, language, userSpecific, tableIds[0], detailRowId)
    setValue(value?.[0])
  }, [variable, dbClient, userEmail, language, userSpecific, tableIds, detailRowId])

  // subscribe to table changes
  useTableSubscription({
    dbClient,
    tableIds,
    callback: handleParseVariableSelect,
  })

  const hasGlobalVariableFlag = variable?.flag === VariableSelectFeatureFlag.GlobalVariable

  // subscribe to global variables changes
  useEffect(() => {
    if (!globalVariableClient || !hasGlobalVariableFlag) return

    const unsubscribe = globalVariableClient.subscribe(() => {
      handleParseVariableSelect()
    })

    return () => {
      unsubscribe()
    }
  }, [handleParseVariableSelect, hasGlobalVariableFlag])

  return value
}
